import { Timestamp, doc, onSnapshot, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { orderBy, limit } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../../FirebaseConfig";
import { SavingOverlay } from "../../components/SavingOverlay";
import { confirmDestructive } from "../../lib/confirm";
import { useCollection } from "../../lib/firestore-hooks";
import { dowEs, formatFechaShort, formatHora } from "../../lib/format";
import type {
  NotificacionDoc,
  NotificationKind,
  UsuarioDoc,
  VueloDoc,
} from "../../lib/types";

const KIND_LABELS: Record<NotificationKind, string> = {
  approval_request: "Por aprobar",
  confirmation: "Aprobado",
  rejection: "Rechazado",
  cancellation: "Cancelado",
  reminder_t24: "Recordatorio 24hrs",
  reminder_t24_pilot: "Recordatorio 24hrs (piloto)",
  pilot_update_t6: "Actualización Meteo y NOTAMs (piloto)",
  modification: "Por aprobar (mod)",
  admin_nudge: "Recordatorio admin",
  conflict_inquiry: "Consulta de horario",
  pilot_assigned: "Piloto asignado",
  legacy_merge: "Migración legacy",
};
function labelForKind(kind: NotificationKind | string): string {
  return KIND_LABELS[kind as NotificationKind] ?? kind;
}

/**
 * Tight one-line summary of what each kind of email actually says.
 * Tracks the templates in functions/email-templates.js (subject +
 * leading paragraph). Keep in sync when the templates change.
 */
const KIND_SUBJECTS: Record<NotificationKind, string> = {
  approval_request: "Aprobar Solicitud de Vuelo",
  confirmation: "Vuelo Aprobado",
  rejection: "Vuelo Rechazado",
  cancellation: "Vuelo Cancelado",
  reminder_t24: "Recordatorio · vuelo mañana",
  reminder_t24_pilot: "Recordatorio piloto · vuelo mañana",
  pilot_update_t6: "Actualización Meteo y NOTAMs · vuelo a las {hora}",
  modification: "Aprobar Modificación de Vuelo",
  admin_nudge: "Recordatorio: vuelo pendiente por aprobar",
  conflict_inquiry: "Consulta de horario · {requesterName}",
  pilot_assigned: "Vuelo asignado · {publicNumber}",
  legacy_merge: "Cuenta legacy fusionada con nuevo UID",
};
const KIND_BODY_SUMMARIES: Record<NotificationKind, string> = {
  approval_request: "Notifica al admin de una solicitud nueva y pide aprobar / rechazar.",
  confirmation: "Confirma al solicitante que el vuelo quedó aprobado (copia admins).",
  rejection: "Avisa al solicitante que su solicitud fue rechazada.",
  cancellation: "Avisa al solicitante que el vuelo fue cancelado.",
  reminder_t24: "Recordatorio de vuelo 24hrs antes al solicitante.",
  reminder_t24_pilot: "Recordatorio de vuelo 24hrs antes al piloto asignado, con clima y NOTAMs.",
  pilot_update_t6: "Actualización de clima y NOTAMs al piloto unas 6 hrs antes del despegue.",
  modification: "Notifica al admin que un vuelo fue modificado para re-aprobar.",
  admin_nudge: "Recordatorio al admin si una solicitud lleva > 24 hrs sin aprobar.",
  conflict_inquiry: "Mensaje del usuario al admin cuando una solicitud choca con otro vuelo o bloqueo.",
  pilot_assigned: "Avisa al piloto cuando admin lo asigna o reasigna a un vuelo.",
  legacy_merge: "Auditoría: cuando un usuario legacy se reconecta con un nuevo UID de Firebase Auth.",
};

/**
 * /admin/notifications — Notificaciones audit log. Server (Cloud
 * Functions) writes; admins read. Shows recent emails with their
 * delivery status. Badges flag waiting-for-action and concluded items
 * so admin can scan what still needs attention.
 */
export function AdminNotificaciones() {
  const navigate = useNavigate();
  const { rows } = useCollection<NotificacionDoc>(
    "Notificaciones",
    orderBy("createdAt", "desc"),
    limit(200)
  );
  const { rows: vuelos } = useCollection<VueloDoc>("Vuelos", orderBy("fecha", "desc"));
  const { rows: usuarios } = useCollection<UsuarioDoc>("Usuarios");
  const [filter, setFilter] = useState<"all" | "sent" | "failed" | "queued">("all");
  const [search, setSearch] = useState("");
  // Pagination state lives up here (before any early-return below) so
  // hook order stays stable across renders. We re-clamp the actual
  // page index after the filtered list is computed.
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [filter, search]);

  // Look up flight summaries (date · route · requester) for the
  // notifications' flightId, so we can show what each row refers to
  // without an extra "ver" column.
  const flightById = useMemo(() => {
    const map = new Map<string, VueloDoc>();
    for (const v of vuelos ?? []) map.set((v as VueloDoc & { id: string }).id, v);
    return map;
  }, [vuelos]);

  // Look up the Usuarios row by email so we can render compact
  // initials in the "A" column. External addresses (cuenta contact
  // emails, ad-hoc recipients) fall back to the part before the @.
  const userByEmail = useMemo(() => {
    const map = new Map<string, UsuarioDoc>();
    for (const u of usuarios ?? []) {
      if (u.email) map.set(u.email.toLowerCase(), u);
    }
    return map;
  }, [usuarios]);
  function shortOne(email: string): { short: string; full: string } {
    const lower = (email ?? "").toLowerCase();
    const u = userByEmail.get(lower);
    if (u) {
      const display = u.initials || u.displayName || email;
      return { short: display, full: `${u.displayName ?? ""} <${email}>`.trim() };
    }
    const at = email.indexOf("@");
    return { short: at > 0 ? email.slice(0, at) : email, full: email };
  }
  // `toAddress` is now possibly a comma-joined list of recipients
  // (single send to many). Split + map each to initials so the column
  // stays compact, and stitch the full addresses into the tooltip.
  function shortRecipient(toAddress: string): { short: string; full: string } {
    const parts = toAddress
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return { short: "—", full: "" };
    const mapped = parts.map(shortOne);
    return {
      short: mapped.map((m) => m.short).join(" · "),
      full: mapped.map((m) => m.full).join("\n"),
    };
  }
  // Classify each recipient by their role *for this specific
  // notification*, not by their account-level role. A user can be
  // both an admin AND the solicitante of a flight — when the
  // confirmation email goes out to "requester + approvers", the
  // requester should appear as "solicitante" (their function in this
  // notification), even if their Usuarios doc happens to also be
  // admin.
  function rolesFor(toAddress: string, flight: VueloDoc | null | undefined): string {
    const parts = toAddress
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const requesterEmail = flight?.requesterEmail?.toLowerCase() ?? null;
    const seen = new Set<string>();
    for (const p of parts) {
      if (requesterEmail && p === requesterEmail) {
        seen.add("solicitante");
        continue;
      }
      const u = userByEmail.get(p);
      if (!u) {
        seen.add("externo");
        continue;
      }
      if (u.isAdmin || u.isSuperAdmin) seen.add("admin");
      else if (u.roles?.includes("pilot") && !u.roles?.includes("user")) {
        seen.add("piloto");
      } else if (u.roles?.includes("user")) seen.add("usuario");
      else seen.add("externo");
    }
    // Stable order so the column reads consistently.
    const order = ["solicitante", "admin", "usuario", "piloto", "externo"];
    return order.filter((r) => seen.has(r)).join(" + ") || "—";
  }

  if (!rows) return <p className="text-sm text-slate-500">Cargando…</p>;

  const counts = {
    all: rows.length,
    queued: rows.filter((r) => r.status === "queued").length,
    sent: rows.filter((r) => r.status === "sent").length,
    failed: rows.filter((r) => r.status === "failed" || r.status === "bounced").length,
  };
  const term = search.trim().toLowerCase();
  const visible = rows
    .filter((r) => {
      if (filter === "all") return true;
      if (filter === "failed") return r.status === "failed" || r.status === "bounced";
      return r.status === filter;
    })
    .filter((r) => {
      if (!term) return true;
      const flight = r.flightId ? flightById.get(r.flightId) : null;
      const flightNum = flight?.publicNumber ?? "";
      const hay = `${r.toAddress ?? ""} ${r.kind ?? ""} ${r.errorDetail ?? ""} ${flightNum}`.toLowerCase();
      return hay.includes(term);
    });

  // Paginate after filtering. Reset to page 0 when the filter or
  // search term changes so the user doesn't end up stranded on an
  // out-of-range page after narrowing the list.
  const PAGE_SIZE = 20;
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageItems = visible.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE,
  );
  const pageStart = visible.length === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const pageEnd = Math.min((currentPage + 1) * PAGE_SIZE, visible.length);

  return (
    <div className="space-y-5">
      <header>
        <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
        <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Notificaciones</h1>
        <p className="mt-1 text-sm text-slate-600">
          Log de cada email enviado o intentado. Server-write only — los
          admins solo leen acá.
        </p>
      </header>

      <EmailTogglesPanel />

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar email, tipo, error o vuelo (ej. 26-001)…"
        className="block w-full max-w-md rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
      />

      <nav className="flex flex-wrap gap-2">
        {(["all", "queued", "sent", "failed"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={
              "rounded-full px-3 py-1 text-xs font-medium transition " +
              (filter === k
                ? "bg-brand-700 text-white"
                : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-brand-50")
            }
          >
            {k === "all" ? "Todos" : k === "queued" ? "En cola" : k === "sent" ? "Enviados" : "Fallidos"}
            <span className={"ml-1.5 text-xs " + (filter === k ? "text-slate-300" : "text-slate-500")}>
              {counts[k]}
            </span>
          </button>
        ))}
      </nav>

      {/* Mobile cards. Desktop table on md+. */}
      <ul className="space-y-2 md:hidden">
        {visible.length === 0 ? (
          <li className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
            Sin notificaciones todavía.
          </li>
        ) : (
          pageItems.map((n) => {
            const flight = n.flightId ? flightById.get(n.flightId) : null;
            return (
              <li
                key={n.id}
                role={n.flightId ? "link" : undefined}
                tabIndex={n.flightId ? 0 : undefined}
                onClick={() =>
                  n.flightId ? navigate(`/admin/flights/${n.flightId}`, { state: { from: "/admin/notifications" } }) : null
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && n.flightId)
                    navigate(`/admin/flights/${n.flightId}`, { state: { from: "/admin/notifications" } });
                }}
                className={
                  "rounded-lg border border-slate-200 bg-white shadow-sm p-3 text-sm "
                  + (n.flightId ? "cursor-pointer hover:bg-sky-50" : "")
                }
              >
                <div className="flex items-baseline justify-between gap-2">
                  <StatusBadge status={n.status} />
                  <span className="text-[11px] text-slate-700">
                    {labelForKind(n.kind)}
                  </span>
                </div>
                <div
                  className="mt-1 font-mono text-xs text-slate-700"
                  title={shortRecipient(n.toAddress).full}
                >
                  {shortRecipient(n.toAddress).short}
                  <span className="ml-2 font-sans text-[11px] text-slate-500">
                    ({rolesFor(n.toAddress, flight)})
                  </span>
                </div>
                {flight ? (
                  <div className="mt-1 text-[11px] text-slate-600">
                    <div>
                      <span className="font-mono">
                        {formatFechaShort(flight.fecha)}
                      </span>{" "}
                      <span className="text-slate-400">
                        ({dowEs(flight.fecha)})
                      </span>{" "}
                      · <span className="font-mono">{formatHora(flight.hora)}</span>
                      {" · "}
                      {flight.aircraftDisplayName}
                      {" · "}
                      <span className="font-mono">
                        {flight.originIcao.replace(/^_/, "")}
                        {" → "}
                        {flight.destinationIcao.replace(/^_/, "")}
                      </span>
                    </div>
                    <div className="mt-0.5">{flight.requesterDisplayName}</div>
                  </div>
                ) : null}
                <div className="mt-1 space-y-0.5 font-mono text-[10px] text-slate-500">
                  <div>creado · {formatTs(n.createdAt)}</div>
                  <div>enviado · {n.sentAt ? formatTs(n.sentAt) : "—"}</div>
                </div>
                {n.errorDetail ? (
                  <div className="mt-1 text-xs text-rose-700">
                    {n.errorDetail}
                  </div>
                ) : n.status === "failed" || n.status === "bounced" ? (
                  <div className="mt-1 text-xs italic text-rose-700/80">
                    Sin detalle — revisar logs.
                  </div>
                ) : null}
                {n.status === "queued" || n.status === "failed" ? (
                  <div
                    className="mt-2 text-right text-xs"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <RetryButton notifId={n.id} />
                  </div>
                ) : null}
              </li>
            );
          })
        )}
      </ul>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="whitespace-nowrap px-2 py-2">Aviso</th>
              <th className="whitespace-nowrap px-2 py-2">Tipo</th>
              <th className="whitespace-nowrap min-w-[8rem] px-3 py-2">A</th>
              <th className="whitespace-nowrap px-2 py-2">Rol</th>
              <th className="whitespace-nowrap border-l border-slate-100 px-3 py-2">
                Creado
              </th>
              <th className="whitespace-nowrap px-3 py-2">Enviado</th>
              <th className="border-l border-slate-100 px-3 py-2">Vuelo</th>
              <th className="w-full px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-500">
                  Sin notificaciones todavía.
                </td>
              </tr>
            ) : (
              pageItems.map((n) => {
                const flight = n.flightId ? flightById.get(n.flightId) : null;
                return (
                  <tr
                    key={n.id}
                    onClick={() =>
                      n.flightId ? navigate(`/admin/flights/${n.flightId}`, { state: { from: "/admin/notifications" } }) : null
                    }
                    className={
                      "hover:bg-slate-50 "
                      + (n.flightId ? "cursor-pointer hover:bg-sky-50" : "")
                    }
                  >
                    <td className="whitespace-nowrap px-2 py-1.5">
                      <StatusBadge status={n.status} />
                    </td>
                    <td className="group/tipo relative whitespace-nowrap px-2 py-1.5 text-xs text-slate-700">
                      {labelForKind(n.kind)}
                      <div className="pointer-events-none invisible absolute left-2 top-full z-30 mt-1 max-w-[20rem] whitespace-normal rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-snug shadow-lg group-hover/tipo:visible">
                        <div className="font-medium text-slate-900">
                          {KIND_SUBJECTS[n.kind as NotificationKind] ??
                            labelForKind(n.kind)}
                        </div>
                        <div className="mt-0.5 text-slate-600">
                          {KIND_BODY_SUMMARIES[n.kind as NotificationKind] ??
                            "Notificación sin descripción."}
                        </div>
                      </div>
                    </td>
                    <td
                      className="whitespace-nowrap min-w-[8rem] px-3 py-1.5 font-mono text-xs text-slate-700"
                      title={shortRecipient(n.toAddress).full}
                    >
                      {shortRecipient(n.toAddress).short}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-xs text-slate-500">
                      {rolesFor(n.toAddress, flight)}
                    </td>
                    <td className="whitespace-nowrap border-l border-slate-100 px-3 py-1.5 font-mono text-xs text-slate-400">
                      {formatTs(n.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-slate-400">
                      {n.sentAt ? formatTs(n.sentAt) : "—"}
                    </td>
                    <td className="group/vuelo relative whitespace-nowrap border-l border-slate-100 px-3 py-1.5 text-xs text-slate-700">
                      {flight ? (
                        <>
                          <span className="font-mono">
                            {flight.publicNumber}
                          </span>
                          <div className="pointer-events-none invisible absolute left-3 top-full z-30 mt-1 whitespace-nowrap rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-tight shadow-lg group-hover/vuelo:visible">
                            <div className="font-mono text-slate-700">
                              {formatFechaShort(flight.fecha)}{" "}
                              <span className="text-slate-400">
                                ({dowEs(flight.fecha)})
                              </span>
                              {" · "}
                              {formatHora(flight.hora)}
                            </div>
                            <div className="mt-0.5 font-medium text-slate-900">
                              {flight.aircraftDisplayName}
                            </div>
                            <div className="mt-0.5 font-mono text-slate-700">
                              {flight.originIcao.replace(/^_/, "")}{" → "}
                              {flight.destinationIcao.replace(/^_/, "")}
                            </div>
                            <div className="mt-0.5 text-slate-700">
                              {flight.requesterDisplayName}
                            </div>
                          </div>
                        </>
                      ) : null}
                      {n.errorDetail ? (
                        <div className="mt-0.5 text-rose-700">
                          {n.errorDetail}
                        </div>
                      ) : n.status === "failed" || n.status === "bounced" ? (
                        <div className="mt-0.5 italic text-rose-700/80">
                          Sin detalle — revisar logs.
                        </div>
                      ) : null}
                    </td>
                    <td
                      className="px-2 py-1.5 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {n.status === "queued" || n.status === "failed" ? (
                        <RetryButton notifId={n.id} />
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — Gmail-style "M–N of T" with prev/next chevrons.
          Hidden on empty results since there's nothing to page through. */}
      {visible.length > 0 ? (
        <div className="flex items-center justify-end gap-3 text-sm text-slate-600">
          <span className="font-mono text-xs">
            {pageStart}–{pageEnd} de {visible.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Página anterior"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Página siguiente"
            >
              ›
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Destructive one-shot tool: wipes everything on the shared calendar
 * from today onward and re-creates events for every future approved
 * flight (with ferries) and active blackout. Rendered in the controls
 * card near the top of /admin/notifications, divider-separated from the
 * two pilot-email toggles (the card has no umbrella title — each control
 * self-describes). The two-step `confirmDestructive` guards the click.
 */
function RebuildCalendarPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<
    { deleted: number; flightsRecreated: number; flightsReconciled: number; blackoutsRecreated: number }
    | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    const ok = await confirmDestructive(
      "Esto borra TODOS los eventos del Google Calendar desde hoy en adelante y los recrea desde Firestore. ¿Continuar?"
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const call = httpsCallable<
        Record<string, never>,
        { deleted: number; flightsRecreated: number; flightsReconciled: number; blackoutsRecreated: number }
      >(functions, "rebuildCalendarFromToday");
      const r = await call({});
      setResult(r.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-slate-200 pt-3 sm:w-56 sm:shrink-0 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
      <SavingOverlay open={busy} label="Reconstruyendo…" />
      <div>
        <span className="block text-sm font-medium text-slate-900">
          Mantención de calendario
        </span>
        <span className="block text-xs text-slate-500">
          Borra y recrea los eventos del Google Calendar desde hoy.
        </span>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={run}
        className="mt-auto self-start rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-800 hover:bg-rose-50 disabled:opacity-50"
      >
        {busy ? "Reconstruyendo…" : "Reconstruir calendario"}
      </button>
      {result ? (
        <p className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
          Listo. Eliminados: {result.deleted}. Vuelos recreados:{" "}
          {result.flightsRecreated}. Bloqueos recreados:{" "}
          {result.blackoutsRecreated}.
          {result.flightsReconciled > 0
            ? ` Vuelos con endAt corregido: ${result.flightsReconciled}.`
            : ""}
        </p>
      ) : null}
      {error ? (
        <p className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function RetryButton({ notifId }: { notifId: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Brief acknowledgement so the click feels responsive — the
  // Firestore snapshot will flip the row's status badge from "failed"
  // to "queued" within ~1s, at which point this toast clears itself.
  const [toast, setToast] = useState(false);
  async function retry() {
    setBusy(true);
    setErr(null);
    setToast(true);
    try {
      const call = httpsCallable(functions, "retryNotification");
      await call({ notificationId: notifId });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
      setToast(false);
    } finally {
      // Drop the toast after 1.2s — by then the snapshot update has
      // refreshed the status badge.
      setTimeout(() => setToast(false), 1200);
    }
  }
  return (
    <div className="space-y-1">
      <button
        type="button"
        disabled={busy}
        onClick={retry}
        className="text-xs text-sky-700 hover:text-sky-900 disabled:opacity-50"
        title="Reintentar envío reconstruyendo el correo desde el vuelo actual"
      >
        {busy ? "Reintentando…" : "Reintentar"}
      </button>
      {toast ? (
        <p className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">
          Encolando…
        </p>
      ) : null}
      {err ? <p className="text-[10px] text-rose-700">{err}</p> : null}
    </div>
  );
}

const STATUS_LABELS: Record<string, string> = {
  queued: "en cola",
  sent: "enviado",
  failed: "error!",
  bounced: "rechazado!",
};

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    queued: "border border-amber-200 bg-amber-50 text-amber-800",
    sent: "border border-emerald-200 bg-emerald-50 text-emerald-800",
    failed: "border border-rose-200 bg-rose-50 text-rose-800",
    bounced: "border border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
        cls[status] ?? "bg-slate-100 text-slate-700"
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function formatTs(t: Timestamp | null): string {
  if (!t) return "";
  const d = t.toDate();
  // es-CL formatter normally separates date and time with a comma —
  // swap it for a thin dot to match the visual rhythm used elsewhere
  // in the app (e.g. fecha · hora · route).
  return new Intl.DateTimeFormat("es-CL", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(d)
    .replace(", ", " · ");
}

/**
 * Three-switch panel that flips the server-side gates for the pilot-
 * facing emails (assignment, T-24 reminder, T-6 Meteo/NOTAMs update). Stored in
 * `Config/email` so backend can read with one doc fetch. Both flags
 * default to ON when the doc is missing — backwards-compat with
 * existing flights that fired before the toggle existed.
 */
function EmailTogglesPanel() {
  const [pilotAssignedEnabled, setPilotAssignedEnabled] = useState(true);
  const [pilotReminderEnabled, setPilotReminderEnabled] = useState(true);
  const [pilotUpdateT6Enabled, setPilotUpdateT6Enabled] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "Config", "email"), (snap) => {
      if (snap.exists()) {
        const d = snap.data() as {
          pilotAssignedEnabled?: boolean;
          pilotReminderEnabled?: boolean;
          pilotUpdateT6Enabled?: boolean;
        };
        setPilotAssignedEnabled(d.pilotAssignedEnabled !== false);
        setPilotReminderEnabled(d.pilotReminderEnabled !== false);
        setPilotUpdateT6Enabled(d.pilotUpdateT6Enabled !== false);
      }
      setLoaded(true);
    });
    return () => unsub();
  }, []);

  async function toggle(field: string, next: boolean) {
    await setDoc(
      doc(db, "Config", "email"),
      { [field]: next },
      { merge: true }
    );
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
        <div className="grid flex-1 gap-2 sm:grid-cols-3">
          <EmailToggleRow
            label="Asignación de vuelo"
            description="Email al piloto al asignarle un vuelo o cambiarlo."
            on={pilotAssignedEnabled}
            disabled={!loaded}
            onChange={(v) => {
              setPilotAssignedEnabled(v);
              void toggle("pilotAssignedEnabled", v);
            }}
          />
          <EmailToggleRow
            label="Recordatorio 24 hrs"
            description="Email al piloto un día antes del vuelo."
            on={pilotReminderEnabled}
            disabled={!loaded}
            onChange={(v) => {
              setPilotReminderEnabled(v);
              void toggle("pilotReminderEnabled", v);
            }}
          />
          <EmailToggleRow
            label="Actualización Meteo y NOTAMs"
            description="Email al piloto unas 6 hrs antes del despegue, con clima y NOTAMs actualizados."
            on={pilotUpdateT6Enabled}
            disabled={!loaded}
            onChange={(v) => {
              setPilotUpdateT6Enabled(v);
              void toggle("pilotUpdateT6Enabled", v);
            }}
          />
        </div>
        <RebuildCalendarPanel />
      </div>
    </section>
  );
}

function EmailToggleRow({
  label,
  description,
  on,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  on: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className={
        "flex items-start gap-3 rounded-md border border-slate-200 p-3 " +
        (disabled ? "opacity-60" : "")
      }
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!on)}
        aria-pressed={on}
        className={
          "mt-0.5 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition " +
          (on ? "bg-brand-700" : "bg-slate-300")
        }
      >
        <span
          className={
            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition " +
            (on ? "translate-x-4" : "translate-x-0.5")
          }
        />
      </button>
      <span>
        <span className="block text-sm font-medium text-slate-900">
          {label}
        </span>
        <span className="block text-xs text-slate-500">{description}</span>
      </span>
    </label>
  );
}
